import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './FacultyProfile.css';
import Toast from './Toast.jsx';
import Loader from './Loader.jsx';

const FacultyProfile = ({ facultyId }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  // Password change state
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [userId, setUserId] = useState('');

  const [profileData, setProfileData] = useState({
    fac_id: '',
    user_id: '',
    fac_name: '',
    email: '',
    phone: '',
    designation: '',
    dept_name: '',
    join_date: '',
    profile_pic_path: null,
    must_change_password: false
  });

  const [editData, setEditData] = useState({
    fac_name: '',
    email: '',
    phone: '',
    designation: ''
  });

  const [isEditing, setIsEditing] = useState(false);

  // Load faculty profile on component mount
  useEffect(() => {
    if (facultyId) {
      loadProfileData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facultyId]);

  const loadProfileData = async () => {
    try {
      setLoading(true);
      console.log('[FacultyProfile] Loading profile for faculty:', facultyId);
      
      const response = await axios.get(`/api/faculty/${facultyId}`);
      console.log('[FacultyProfile] Profile loaded:', response.data);
      
      setProfileData(response.data);
      setUserId(response.data.user_id || '');
      setMustChangePassword(response.data.must_change_password || false);
      // Auto-show password form on first login
      if (response.data.must_change_password) {
        setShowPasswordForm(true);
      }
      setEditData({
        fac_name: response.data.fac_name || '',
        email: response.data.email || '',
        phone: response.data.phone || '',
        designation: response.data.designation || ''
      });

      // Load profile picture if exists
      if (response.data.profile_pic_path) {
        setImagePreview(`/uploads/${response.data.profile_pic_path.split('/').pop()}`);
      }

      setToast({ type: 'success', message: 'Profile loaded successfully' });
    } catch (error) {
      console.error('[FacultyProfile] Error loading profile:', error);
      setToast({ 
        type: 'error', 
        message: `Failed to load profile: ${error.response?.data?.error || error.message}` 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        setToast({ 
          type: 'error', 
          message: 'Please select a valid image file (JPG, PNG, GIF, or WebP)' 
        });
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setToast({ 
          type: 'error', 
          message: 'Image size must be less than 5MB' 
        });
        return;
      }

      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadImage = async () => {
    if (!selectedFile) {
      setToast({ type: 'error', message: 'Please select an image first' });
      return;
    }

    try {
      setSaving(true);
      console.log('[FacultyProfile] Uploading profile image...');

      const formData = new FormData();
      formData.append('profile_pic', selectedFile);

      const response = await axios.put(
        `/api/faculty/${facultyId}/profile-pic`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      console.log('[FacultyProfile] Image uploaded:', response.data);
      setToast({ type: 'success', message: 'Profile picture updated successfully!' });
      
      // Update profile data with new image path
      setProfileData({
        ...profileData,
        profile_pic_path: response.data.profile_pic_path
      });
      
      setSelectedFile(null);
      loadProfileData();
    } catch (error) {
      console.error('[FacultyProfile] Error uploading image:', error);
      setToast({ 
        type: 'error', 
        message: `Failed to upload image: ${error.response?.data?.error || error.message}` 
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditData({
      ...editData,
      [name]: value
    });
  };

  const handleSaveChanges = async () => {
    try {
      setSaving(true);
      console.log('[FacultyProfile] Saving profile changes:', editData);

      const response = await axios.put(
        `/api/faculty/${facultyId}`,
        editData
      );

      console.log('[FacultyProfile] Profile updated:', response.data);
      setToast({ type: 'success', message: 'Profile updated successfully!' });
      
      // Reload profile data
      loadProfileData();
      setIsEditing(false);
    } catch (error) {
      console.error('[FacultyProfile] Error updating profile:', error);
      setToast({ 
        type: 'error', 
        message: `Failed to update profile: ${error.response?.data?.error || error.message}` 
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    // Reset edit data to original
    setEditData({
      fac_name: profileData.fac_name || '',
      email: profileData.email || '',
      phone: profileData.phone || '',
      designation: profileData.designation || ''
    });
  };

  // ── Password Change ──
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitPasswordChange = async () => {
    // Validation
    if (!mustChangePassword && !passwordData.current_password) {
      setToast({ type: 'error', message: 'Current password is required' });
      return;
    }
    if (!passwordData.new_password) {
      setToast({ type: 'error', message: 'New password is required' });
      return;
    }
    if (passwordData.new_password.length < 4) {
      setToast({ type: 'error', message: 'Password must be at least 4 characters' });
      return;
    }
    if (passwordData.new_password !== passwordData.confirm_password) {
      setToast({ type: 'error', message: 'Passwords do not match' });
      return;
    }

    try {
      setChangingPassword(true);

      // Resolve user_id: prefer from profile, fallback to localStorage
      let resolvedUserId = userId;
      if (!resolvedUserId) {
        try {
          const stored = JSON.parse(localStorage.getItem('user') || '{}');
          resolvedUserId = stored.user_id || '';
        } catch (e) { /* ignore */ }
      }

      if (!resolvedUserId) {
        setToast({ type: 'error', message: 'User ID not found. Please re-login.' });
        return;
      }

      const payload = {
        user_id: resolvedUserId,
        new_password: passwordData.new_password
      };
      // Only include current_password when it's NOT a first-login forced change
      if (!mustChangePassword) {
        payload.current_password = passwordData.current_password;
      }

      await axios.post('/api/change-password', payload);

      setToast({ type: 'success', message: 'Password changed successfully!' });
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      setShowPasswordForm(false);
      setMustChangePassword(false);
    } catch (error) {
      setToast({
        type: 'error',
        message: error.response?.data?.error || 'Failed to change password'
      });
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="faculty-profile-container">
      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}

      <div className="profile-header">
        <h2>👤 My Profile</h2>
        <p>Manage your personal information and profile picture</p>
      </div>

      <div className="profile-content">
        {/* Profile Picture Section */}
        <div className="profile-picture-section">
          <div className="profile-picture-container">
            {imagePreview ? (
              <img
                src={imagePreview}
                alt="Profile"
                className="profile-picture"
              />
            ) : (
              <div className="profile-picture-placeholder">
                <span className="placeholder-icon">📷</span>
              </div>
            )}
          </div>

          <div className="picture-upload-controls">
            <label htmlFor="image-input" className="upload-button">
              Choose Image
            </label>
            <input
              id="image-input"
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              style={{ display: 'none' }}
              disabled={saving}
            />
            
            {selectedFile && (
              <button
                className="save-image-button"
                onClick={handleUploadImage}
                disabled={saving}
              >
                {saving ? 'Uploading...' : 'Upload Image'}
              </button>
            )}
          </div>
          <p className="upload-hint">
            JPG, PNG, GIF or WebP • Max 5MB
          </p>
        </div>

        {/* Profile Information Section */}
        <div className="profile-info-section">
          {!isEditing ? (
            <>
              <div className="profile-info-grid">
                <div className="info-item">
                  <label>Faculty ID</label>
                  <p>{profileData.fac_id}</p>
                </div>

                <div className="info-item">
                  <label>Full Name</label>
                  <p>{profileData.fac_name}</p>
                </div>

                <div className="info-item">
                  <label>Email Address</label>
                  <p>{profileData.email}</p>
                </div>

                <div className="info-item">
                  <label>Phone Number</label>
                  <p>{profileData.phone || 'Not provided'}</p>
                </div>

                <div className="info-item">
                  <label>Designation</label>
                  <p>{profileData.designation}</p>
                </div>

                <div className="info-item">
                  <label>Department</label>
                  <p>{profileData.dept_name}</p>
                </div>

                <div className="info-item">
                  <label>Join Date</label>
                  <p>
                    {profileData.join_date
                      ? new Date(profileData.join_date).toLocaleDateString()
                      : 'Not provided'}
                  </p>
                </div>
              </div>

              <button
                className="edit-profile-button"
                onClick={() => setIsEditing(true)}
              >
                ✏️ Edit Profile
              </button>
            </>
          ) : (
            <>
              <form className="profile-edit-form">
                <div className="form-group">
                  <label htmlFor="fac_name">Full Name</label>
                  <input
                    type="text"
                    id="fac_name"
                    name="fac_name"
                    value={editData.fac_name}
                    onChange={handleEditChange}
                    placeholder="Enter your full name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={editData.email}
                    onChange={handleEditChange}
                    placeholder="Enter your email"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={editData.phone}
                    onChange={handleEditChange}
                    placeholder="Enter your phone number"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="designation">Designation</label>
                  <input
                    type="text"
                    id="designation"
                    name="designation"
                    value={editData.designation}
                    onChange={handleEditChange}
                    placeholder="Enter your designation"
                  />
                </div>
              </form>

              <div className="form-actions">
                <button
                  className="save-button"
                  onClick={handleSaveChanges}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : '💾 Save Changes'}
                </button>
                <button
                  className="cancel-button"
                  onClick={handleCancel}
                  disabled={saving}
                >
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Password Change Section */}
      <div className="password-section">
        {mustChangePassword && (
          <div className="password-alert">
            <span className="alert-icon">⚠️</span>
            <div>
              <strong>Password Change Required</strong>
              <p>You are using an auto-generated password. Please set a new password to continue.</p>
            </div>
          </div>
        )}

        {!showPasswordForm ? (
          <button
            className="change-password-toggle"
            onClick={() => setShowPasswordForm(true)}
          >
            🔒 Change Password
          </button>
        ) : (
          <div className="password-form-card">
            <div className="password-form-header">
              <h3>🔒 Change Password</h3>
              {!mustChangePassword && (
                <button
                  className="password-close-btn"
                  onClick={() => {
                    setShowPasswordForm(false);
                    setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
                  }}
                >
                  ✕
                </button>
              )}
            </div>

            <div className="password-form-body">
              {!mustChangePassword && (
                <div className="form-group">
                  <label htmlFor="current_password">Current Password</label>
                  <input
                    type="password"
                    id="current_password"
                    name="current_password"
                    value={passwordData.current_password}
                    onChange={handlePasswordChange}
                    placeholder="Enter current password"
                    autoComplete="current-password"
                  />
                </div>
              )}

              <div className="form-group">
                <label htmlFor="new_password">New Password</label>
                <input
                  type="password"
                  id="new_password"
                  name="new_password"
                  value={passwordData.new_password}
                  onChange={handlePasswordChange}
                  placeholder="Enter new password (min 4 characters)"
                  autoComplete="new-password"
                />
              </div>

              <div className="form-group">
                <label htmlFor="confirm_password">Confirm New Password</label>
                <input
                  type="password"
                  id="confirm_password"
                  name="confirm_password"
                  value={passwordData.confirm_password}
                  onChange={handlePasswordChange}
                  placeholder="Confirm new password"
                  autoComplete="new-password"
                />
              </div>

              {passwordData.new_password && passwordData.confirm_password && (
                <div className={`password-match-indicator ${passwordData.new_password === passwordData.confirm_password ? 'match' : 'no-match'}`}>
                  {passwordData.new_password === passwordData.confirm_password
                    ? '✓ Passwords match'
                    : '✗ Passwords do not match'}
                </div>
              )}

              <div className="form-actions">
                <button
                  className="save-button"
                  onClick={handleSubmitPasswordChange}
                  disabled={changingPassword}
                >
                  {changingPassword ? 'Changing...' : '🔑 Update Password'}
                </button>
                {!mustChangePassword && (
                  <button
                    className="cancel-button"
                    onClick={() => {
                      setShowPasswordForm(false);
                      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
                    }}
                    disabled={changingPassword}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FacultyProfile;
